import {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../hooks';
import { motion } from 'framer-motion';

//Components
import WorkflowsFooter from 'components/WorkflowsFooter/WorkflowsFooter';

//Redux

import {
  selectCustomConfigParams,
  selectFeatureFlags,
  selectIsBuilderPreview,
  selectIsMobileSDK,
  selectSelectedArticle,
} from 'reducers/workflows';
import {
  selectHeaderLogoIcon,
  selectLegibleButtonOutlineColor,
} from 'reducers/config';
import {
  selectIsArticlePage,
  selectVisualViewportOffset,
  selectIsMobile,
  selectWidgetStyle,
} from 'reducers/main';

//Styles
import {
  Container,
  ContentContainer,
  ModalHeader,
  ModalHeaderButtonsContainer,
  ModalHeaderText,
  ModalHeaderTextContainer,
} from './styles';
import ScreenReaderNotifications from 'components/ScreenReaderNotifications';

import UAParser from 'ua-parser-js';
import ImageViewer from 'components/ImageViewer';
import { PrivacyConsentModal } from 'components/PrivacyConsent/PrivacyConsentModal';
import { debounce } from 'utils/debounce';
import { CsatSurveyDrawer } from 'components/Csat/CsatSurveyDrawer';
import { EndLiveChatModal } from './EndLiveChatModal';
import { BannerImage } from 'components/BannerImage/BannerImage';
import { ModalActions } from './ModalActions';

interface ModalProps {
  /** Text that is display on left side of the header */
  headerText: string;
}

export const Modal = motion.create(
  forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>(function Modal(
    { children, headerText },
    ref,
  ) {
    const featureFlags = useAppSelector(selectFeatureFlags);
    const customConfigParams = useAppSelector(selectCustomConfigParams);
    const headerLogoIcon = useAppSelector(selectHeaderLogoIcon);
    const isBuilderPreview = useAppSelector(selectIsBuilderPreview);
    const isMobile = useAppSelector(selectIsMobile);
    const visualViewportOffset = useAppSelector(selectVisualViewportOffset);
    const selectedArticle = useAppSelector(selectSelectedArticle);
    const isArticlePage = useAppSelector(selectIsArticlePage);
    const isMobileSDK = useAppSelector(selectIsMobileSDK);
    const widgetStyle = useAppSelector(selectWidgetStyle);
    const legibleButtonOutlineColor = useAppSelector(
      selectLegibleButtonOutlineColor,
    );

    const hideNav = customConfigParams?.hide_nav ?? '';
    const shouldHideNavBar =
      (!!isArticlePage && !!selectedArticle) ||
      hideNav === 'true' ||
      widgetStyle === 'floating_bar';
    const browser = new UAParser().getBrowser().name || '';
    const shouldRespositionHeader =
      featureFlags.includes('mobile_header_reposition') &&
      isMobile &&
      ['Mobile Safari', 'Safari', 'WebKit'].includes(browser);

    const headerTitleRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLHeadingElement>(null);

    const addHeaderStyle = useMemo(
      () =>
        debounce((offset: number) => {
          if (headerRef.current) {
            if (offset > 0) {
              headerRef.current.style.top = `${offset}px`;
              headerRef.current.style.position = 'fixed';
              headerRef.current.style.zIndex = '2147483647';
            } else {
              headerRef.current.style.removeProperty('top');
              headerRef.current.style.removeProperty('position');
              headerRef.current.style.removeProperty('z-index');
            }
          }
        }),
      [],
    );

    // makes tab operability start from header, A11Y only
    useEffect(() => {
      headerTitleRef.current?.focus();
    }, []);

    // keep header on top of the screen when on specific mobile browsers
    useEffect(() => {
      const handleUrlScrollChange = () => {
        // embed.ts does not apply to url widgets so grab offset from window
        addHeaderStyle(window.visualViewport?.offsetTop || 0);
      };

      if (shouldRespositionHeader) {
        if (window.self !== window.top) {
          // embed.ts sets up the event listener when in the iframe
          addHeaderStyle(visualViewportOffset.offsetTop);
        } else {
          window.visualViewport?.addEventListener(
            'scroll',
            handleUrlScrollChange,
          );
        }
      } else {
        // clear styling in case of widget resize from isMobile to !isMobile
        addHeaderStyle(0);
      }

      return () => {
        window.visualViewport?.removeEventListener(
          'scroll',
          handleUrlScrollChange,
        );
      };
    }, [
      addHeaderStyle,
      shouldRespositionHeader,
      visualViewportOffset.offsetTop,
    ]);

    return (
      <Container
        isBuilderPreview={isBuilderPreview}
        isMobileSDK={isMobileSDK}
        legibleButtonOutlineColor={legibleButtonOutlineColor}
        ref={ref}
        widgetStyle={widgetStyle}
      >
        {!shouldHideNavBar && (
          <ModalHeader
            data-testid='modal-header'
            id='header'
            noBorderRadius={isMobile || isBuilderPreview}
            ref={headerRef}
          >
            <ModalHeaderTextContainer>
              <ImageContainer alt='' src={headerLogoIcon} />
              <ModalHeaderText ref={headerTitleRef} tabIndex={0}>
                {headerText}
              </ModalHeaderText>
            </ModalHeaderTextContainer>
            <ModalHeaderButtonsContainer>
              <ModalActions />
            </ModalHeaderButtonsContainer>
          </ModalHeader>
        )}
        <BannerImage />
        <ContentContainer
          includeBorderRadius={
            !isMobile && !isBuilderPreview && shouldHideNavBar
          }
        >
          {children}
        </ContentContainer>
        <WorkflowsFooter />
        <ScreenReaderNotifications />
        <ImageViewer />
        <PrivacyConsentModal />
        <CsatSurveyDrawer />
        <EndLiveChatModal />
      </Container>
    );
  }),
);

const ImageContainer = styled('img')`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: contain;
`;
